import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import { BOT_DESCRIPTION_MAX_LENGTH } from "@rakazo/contracts";
import { Textarea } from "@rakazo/ui-web";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@rakazo/ui-web/components/ui/tabs";
import { InstructionSettings } from "../../components/InstructionSettings";

/**
 * The bot's own words: a short description other bots read, and the long
 * instructions it follows. They sit in tabs so the profile header stays close
 * to the controls and neither text block pushes the other off screen.
 */
export function BotProfileText({
  bot,
  description,
  descriptionId,
  onDescriptionChange,
  onDescriptionCommit,
}: {
  bot: Bot;
  description: string;
  descriptionId: string;
  onDescriptionChange: (value: string) => void;
  onDescriptionCommit: () => void;
}) {
  const { t } = useLingui();
  return (
    <Tabs defaultValue="description" className="mt-0 gap-3" data-testid="bot-profile-text">
      <TabsList aria-label={t`Bot profile`} className="h-auto w-full gap-1 p-1">
        <TabsTrigger value="description" className="min-h-11 flex-1 px-3 text-sm">
          <Trans>Description</Trans>
        </TabsTrigger>
        <TabsTrigger value="instructions" className="min-h-11 flex-1 px-3 text-sm">
          <Trans>Instructions</Trans>
        </TabsTrigger>
      </TabsList>
      {/* Both panels stay mounted, so a tab switch never drops an unsaved draft. */}
      <TabsContent value="description" keepMounted className="pt-1">
        <p id={`${descriptionId}-hint`} className="mb-2 text-xs text-muted-foreground">
          <Trans>How other bots see it</Trans>
        </p>
        <Textarea
          id={descriptionId}
          aria-label={t`Description`}
          aria-describedby={`${descriptionId}-hint`}
          value={description}
          maxLength={BOT_DESCRIPTION_MAX_LENGTH}
          onChange={(event) => onDescriptionChange(event.target.value)}
          onBlur={onDescriptionCommit}
          placeholder={t`What this bot is for`}
          rows={3}
          className="min-h-24 resize-y rounded-xl border-border bg-background px-4 py-3 font-normal text-base leading-relaxed md:text-base"
        />
      </TabsContent>
      <TabsContent value="instructions" keepMounted className="pt-1">
        <InstructionSettings key={bot.id} bot={bot} heading={false} />
      </TabsContent>
    </Tabs>
  );
}
